import { reportSections, type TradingReport } from './reportModel';

const W = 720;
const H = 180;
const PAD = { left: 56, right: 12, top: 10, bottom: 10 };

/** MT5's trading report: results, drawdowns, trade counts, streaks, the balance curve and results by symbol. */
export function TradingReportView({
  report,
  money,
}: {
  report: TradingReport;
  money: (v: number) => string;
}) {
  const sections = reportSections(report, money);
  const curve = report.balanceCurve;
  return (
    <div className="flex flex-col gap-4 p-3" data-testid="trading-report">
      {report.all.trades === 0 && <p className="text-sm text-muted">No trades were closed in this period.</p>}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {sections.map((s) => (
          <section key={s.title} aria-label={s.title}>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">{s.title}</h3>
            <dl className="text-sm">
              {s.lines.map((l) => (
                <div
                  key={l.label}
                  className="flex justify-between gap-3 border-b border-line/40 py-0.5"
                  title={l.hint}
                >
                  <dt className="text-muted">{l.label}</dt>
                  <dd
                    className={`num text-right font-semibold ${l.tone === 'up' ? 'text-up' : l.tone === 'down' ? 'text-down' : ''}`}
                  >
                    {l.value}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>

      {curve.length > 1 && <BalanceCurve points={curve} money={money} />}

      {report.bySymbol.length > 0 && (
        <table className="w-full text-sm" aria-label="Results by symbol">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-2 py-1 font-normal">Symbol</th>
              <th className="px-2 py-1 text-right font-normal">Trades</th>
              <th className="px-2 py-1 text-right font-normal">Win rate</th>
              <th className="px-2 py-1 text-right font-normal">Volume</th>
              <th className="px-2 py-1 text-right font-normal">Profit factor</th>
              <th className="px-2 py-1 text-right font-normal">Net profit</th>
            </tr>
          </thead>
          <tbody>
            {report.bySymbol.map((s) => (
              <tr key={s.symbol} className="border-b border-line/40" data-testid="report-symbol">
                <td className="px-2 py-1 font-semibold">{s.symbol}</td>
                <td className="num px-2 py-1 text-right">{s.stats.trades}</td>
                <td className="num px-2 py-1 text-right">{s.stats.winRate.toFixed(2)}%</td>
                <td className="num px-2 py-1 text-right">{s.stats.volume}</td>
                <td className="num px-2 py-1 text-right">
                  {s.stats.profitFactor === null || s.stats.profitFactor === undefined
                    ? '—'
                    : s.stats.profitFactor.toFixed(2)}
                </td>
                <td
                  className={`num px-2 py-1 text-right font-semibold ${s.stats.netProfit > 0 ? 'text-up' : s.stats.netProfit < 0 ? 'text-down' : ''}`}
                >
                  {money(s.stats.netProfit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BalanceCurve({
  points,
  money,
}: {
  points: TradingReport['balanceCurve'];
  money: (v: number) => string;
}) {
  const values = points.map((p) => p.balance);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const x = (i: number) => PAD.left + (i / (points.length - 1)) * (W - PAD.left - PAD.right);
  const y = (v: number) => PAD.top + (1 - (v - lo) / span) * (H - PAD.top - PAD.bottom);
  const line = points.map((p, i) => `${x(i).toFixed(1)},${y(p.balance).toFixed(1)}`).join(' ');
  return (
    <figure>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-44 w-full"
        role="img"
        aria-label={`Balance from ${money(values[0]!)} to ${money(values[values.length - 1]!)}, lowest ${money(lo)}, highest ${money(hi)}`}
      >
        <text x={4} y={PAD.top + 8} className="fill-current text-[10px] text-muted">
          {money(hi)}
        </text>
        <text x={4} y={H - PAD.bottom} className="fill-current text-[10px] text-muted">
          {money(lo)}
        </text>
        <polyline points={line} fill="none" stroke="var(--accent)" strokeWidth={2} />
      </svg>
      <figcaption className="text-xs text-muted">
        Balance after each deal (trades, deposits and withdrawals).
      </figcaption>
    </figure>
  );
}
